import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import axios from "axios";
import { getCookie } from "../jwt/_helpers/cookie";

const PROGRESS_INCREMENT = 0.05;
const MIN_COMPLETION_RATIO = 0.95;
const MIN_UNIQUE_SECONDS_PER_CHUNK = 5;

const mergeIntervals = (intervals, candidate) => {
  if (!candidate || candidate[1] <= candidate[0]) {
    return intervals;
  }

  const merged = [];
  const sorted = [...intervals, candidate].sort((a, b) => a[0] - b[0]);

  sorted.forEach(([start, end]) => {
    if (!merged.length || start > merged[merged.length - 1][1]) {
      merged.push([start, end]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
    }
  });

  return merged;
};

const getIntervalTotal = (intervals) =>
  intervals.reduce((total, [start, end]) => total + Math.max(0, end - start), 0);

const sanitizeRange = (start, end, duration) => {
  const safeStart = Math.max(0, Math.min(start, duration ?? start));
  const safeEnd = Math.max(safeStart, Math.min(end, duration ?? end));
  return [safeStart, safeEnd];
};
const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const ListenTracks = ({ track_urls, setForceUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progressByTrack, setProgressByTrack] = useState({});
  const [loadingTracks, setLoadingTracks] = useState({});
  const [failedTracks, setFailedTracks] = useState({});
  const progressCacheRef = useRef({});
  const listenedSecondsRef = useRef({});
  const listenedIntervalsRef = useRef({});
  const lastTimeRef = useRef({});
  const trackDurationsRef = useRef({});
  const audioRefs = useRef({});
  const pendingRefreshRef = useRef(false);

  const toggleModal = () => {
    const wasOpen = isModalOpen;
    setIsModalOpen(!isModalOpen);

    if (
      wasOpen && pendingRefreshRef.current
    ) {
      setTimeout(() => {
        setForceUpdate(prev => prev + 1);
        pendingRefreshRef.current = false;
      }, 150);
    }
  };

  const recordUniqueListening = (trackId, start, end, duration) => {
    if (!trackId || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return listenedSecondsRef.current[trackId] || 0;
    }

    const [safeStart, safeEnd] = sanitizeRange(start, end, duration);
    const currentIntervals = listenedIntervalsRef.current[trackId] || [];
    const previousTotal = getIntervalTotal(currentIntervals);
    const durationBound = duration ?? safeEnd;

    const updatedIntervals = mergeIntervals(currentIntervals, [safeStart, safeEnd]);
    const nextTotal = Math.min(getIntervalTotal(updatedIntervals), durationBound);

    listenedIntervalsRef.current[trackId] = updatedIntervals;
    listenedSecondsRef.current[trackId] = nextTotal;

    return { previousTotal, nextTotal };
  };

  const hydrateProgressFromBackend = (trackId, listenedSeconds, duration) => {
    if (!trackId || !Number.isFinite(listenedSeconds) || listenedSeconds <= 0) {
      return;
    }

    const safeDuration = duration ?? trackDurationsRef.current[trackId];
    if (!safeDuration || safeDuration <= 0) {
      return;
    }

    const safeSeconds = Math.min(listenedSeconds, safeDuration);
    const existingSeconds = listenedSecondsRef.current[trackId] || 0;
    if (safeSeconds <= existingSeconds) {
      return;
    }

    listenedIntervalsRef.current[trackId] = [[0, safeSeconds]];
    listenedSecondsRef.current[trackId] = safeSeconds;
  };

  const describeMediaError = (error) => {
    if (!error) {
      return "Unknown audio error.";
    }
    switch (error.code) {
      case error.MEDIA_ERR_ABORTED:
        return "Playback aborted. Please try again.";
      case error.MEDIA_ERR_NETWORK:
        return "Network error while downloading the audio.";
      case error.MEDIA_ERR_DECODE:
        return "Audio decode error. The file may be corrupted or in an unsupported format.";
      case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
        return "Audio source format is not supported.";
      default:
        return "Unexpected audio error.";
    }
  };

  const handleAudioError = (track, event) => {
    if (event && typeof event.persist === "function") {
      event.persist();
    }

    const mediaElement = event?.target;
    const nativeError = mediaElement?.error;
    const description = describeMediaError(nativeError);

    const fallbackSource = getAudioSource(track);
    const fallbackMime = getMimeType(track);

    console.error("[ListenTracks] Audio error", {
      trackId: track?.id,
      trackName: track?.name,
      source: fallbackSource,
      mimeType: fallbackMime,
      readyState: mediaElement?.readyState,
      networkState: mediaElement?.networkState,
      errorCode: nativeError?.code
    });


    setFailedTracks(prev => ({
      ...prev,
      [track.id]: true
    }));
    setLoadingTracks(prev => ({
      ...prev,
      [track.id]: false
    }));


    if (nativeError?.code !== nativeError?.MEDIA_ERR_NETWORK) {
      console.log(`Unable to play "${track.name}". ${description}`);
    }
  };

  const handlePlayClick = (trackId) => {
    const audioElements = document.querySelectorAll('[id^="track_play_"]');
    audioElements.forEach((audio) => {
      if (!audio.paused && audio.id !== `track_play_${trackId}`) {
        audio.pause();
      }
    });
  };

  const getAudioSource = (track) => {
    if (track.mp3_resource && typeof track.mp3_resource === "string") {
      const trimmed = track.mp3_resource.trim();
      return trimmed || null;
    }

    if (track.resource && typeof track.resource === "string") {
      const trimmed = track.resource.trim();
      return trimmed || null;
    }
    
    return null;
  };

  const getMimeType = (track) => {
    if (track.mp3_resource) {
      return "audio/mpeg";
    }
    if (track.resource) {
      return "audio/wav";
    }
    return undefined;
  };

  const secondsToHHMMSS = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };


  const updateTrackProgressState = (trackId, percent) => {
    setProgressByTrack(prev => {
      const current = prev[trackId] ?? 0;
      if (current >= 100) {
        return prev;
      }
      if (percent <= current) {
        return prev;
      }
      const nextValue = Math.min(100, Math.round(percent));
      return {
        ...prev,
        [trackId]: nextValue
      };
    });
  };

  const reportListeningProgress = async ({
    trackId,
    percentComplete,
    listenedSeconds
  }) => {
    const currentProgress = progressByTrack[trackId] ?? 0;
    if (currentProgress >= 100) {
      return;
    }
    
    const nextPercent =
      percentComplete >= 1
        ? 100
        : Math.floor(percentComplete * 100);
    const roundedSeconds = Math.round(listenedSeconds);
    const durationString = secondsToHHMMSS(roundedSeconds);
    const listenedPercentageDecimal = parseFloat(nextPercent.toFixed(2));

    updateTrackProgressState(trackId, nextPercent);

    const tokenAPI = getCookie("token");
    
    try {
      await axios.post(
        `${API_URL}tracks/update-listened-duration/`,
        {
          track_id: trackId,
          listened_duration: durationString,
          listened_percentage: listenedPercentageDecimal,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenAPI}`,
            "x-api-key": X_API_KEY,
            "Content-Type": "application/json"
          }
        }
      );
      if (nextPercent >= 33 && currentProgress < 33) {
        pendingRefreshRef.current = true;
      }
    } catch (error) {
      console.error(`[ListenTracks] Failed to save progress for track ${trackId}:`, error);
    }
  };

  const syncProgressFromProps = () => {
    if (!track_urls || track_urls.length === 0) {
      return;
    }

    setProgressByTrack(prev => {
      const updatedProgress = { ...prev };
      let hasUpdates = false;

      track_urls.forEach((track) => {
        if (track.listened_duration && track.id) {
          const currentPercent = prev[track.id] ?? 0;

          if (currentPercent >= 100) {
            return;
          }
          
          const listenedSeconds = track.listened_duration;
          const duration = trackDurationsRef.current[track.id];
          
          if (duration && duration > 0) {
            const percent = Math.min(100, Math.round((listenedSeconds / duration) * 100));

            if (percent > currentPercent || currentPercent === 0) {
              updatedProgress[track.id] = percent;
              hasUpdates = true;
            }

            hydrateProgressFromBackend(track.id, listenedSeconds, duration);
          }
        }
      });

      return hasUpdates ? updatedProgress : prev;
    });
  };


  useEffect(() => {

    const hasDurations = Object.keys(trackDurationsRef.current).length > 0;
    if (hasDurations) {
      syncProgressFromProps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track_urls]);

  const handleAudioPlay = (track, event) => {
    handlePlayClick(track.id);
    lastTimeRef.current[track.id] = event?.target?.currentTime || 0;
  };

  const handleLoadedMetadata = (track, event) => {
    const audioElement = event?.target;
    if (audioElement?.duration && !isNaN(audioElement.duration)) {
      trackDurationsRef.current[track.id] = audioElement.duration;
      setLoadingTracks(prev => ({
        ...prev,
        [track.id]: false
      }));
      setFailedTracks(prev => {
        const updated = { ...prev };
        delete updated[track.id];
        return updated;
      });
      syncProgressFromProps();
    }
  };

  const handleLoadStart = (track) => {
    setLoadingTracks(prev => ({
      ...prev,
      [track.id]: true
    }));
    setFailedTracks(prev => {
      const updated = { ...prev };
      delete updated[track.id];
      return updated;
    });
  };

  const handleCanPlay = (track) => {
    setLoadingTracks(prev => ({
      ...prev,
      [track.id]: false
    }));
  };

  const retryLoadTrack = (track) => {
    const audioElement = audioRefs.current[track.id];
    if (audioElement) {
      setFailedTracks(prev => {
        const updated = { ...prev };
        delete updated[track.id];
        return updated;
      });
      setLoadingTracks(prev => ({
        ...prev,
        [track.id]: true
      }));

      audioElement.load();
    }
  };

  const handleProgressUpdate = (track, event) => {
    const audioElement = event?.target;
    if (!audioElement || !audioElement.duration || Number.isNaN(audioElement.duration)) {
      return;
    }

    const currentTime = audioElement.currentTime;
    const previousTime =
      lastTimeRef.current[track.id] !== undefined
        ? lastTimeRef.current[track.id]
        : currentTime;

    const rawDelta = currentTime - previousTime;
    const isSeek = Math.abs(rawDelta) > 1.5 || rawDelta < 0;
    if (isSeek || audioElement.paused) {
      lastTimeRef.current[track.id] = currentTime;
      return;
    }

    if (rawDelta <= 0) {
      lastTimeRef.current[track.id] = currentTime;
      return;
    }

    const { previousTotal, nextTotal } = recordUniqueListening(
      track.id,
      previousTime,
      currentTime,
      audioElement.duration
    );
    lastTimeRef.current[track.id] = currentTime;

    const chunkThreshold = Math.min(
      MIN_UNIQUE_SECONDS_PER_CHUNK,
      Math.max(1, audioElement.duration * 0.1)
    );

    if (nextTotal < chunkThreshold && previousTotal < chunkThreshold) {
      return;
    }

    const percentComplete = Math.min(
      1,
      (listenedSecondsRef.current[track.id] || 0) / audioElement.duration
    );
    const cacheKey = `${track.id}`;
    const lastSent = progressCacheRef.current[cacheKey] || 0;

    if (percentComplete < lastSent + PROGRESS_INCREMENT && percentComplete !== 1) {
      return;
    }

    progressCacheRef.current[cacheKey] = percentComplete;
    reportListeningProgress({
      trackId: track.id,
      percentComplete,
      listenedSeconds: listenedSecondsRef.current[track.id]
    });
  };

  const handleEnded = (track, event) => {
    const duration = event?.target?.duration || event?.target?.currentTime || 0;
    const previousTime =
      lastTimeRef.current[track.id] !== undefined
        ? lastTimeRef.current[track.id]
        : 0;

    recordUniqueListening(track.id, previousTime, duration, duration);

    const actualListened = Math.min(
      listenedSecondsRef.current[track.id] || 0,
      duration
    );

    const completionRatio =
      duration > 0 ? actualListened / duration : 0;

    const hasMetCompletion = completionRatio >= MIN_COMPLETION_RATIO;
    const percentToReport = hasMetCompletion ? 1 : completionRatio;
    const secondsToReport = hasMetCompletion ? duration : actualListened;

    progressCacheRef.current[track.id] = percentToReport;
    listenedSecondsRef.current[track.id] = secondsToReport;
    lastTimeRef.current[track.id] = secondsToReport;

    reportListeningProgress({
      trackId: track.id,
      percentComplete: percentToReport,
      listenedSeconds: secondsToReport
    });
  };

  const overallProgressPercent = useMemo(() => {
    if (!track_urls || track_urls.length === 0) {
      return 0;
    }
    const total = track_urls.reduce((acc, track) => {
      return acc + (progressByTrack[track.id] ?? 0);
    }, 0);
    return Math.round(total / track_urls.length);
  }, [track_urls, progressByTrack]);

  const renderProgressBar = (value) => (
    <div style={{ width: "100%", marginTop: "8px" }}>
      <div
        style={{
          width: "100%",
          height: "8px",
          borderRadius: "999px",
          backgroundColor: "#e9ecef",
          overflow: "hidden",
          position: "relative"
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
        {value}%
      </div>
    </div>
  );

  if (!track_urls || track_urls.length === 0) {
    return <span style={{ color: "#999" }}>No tracks</span>;
  }

  return (
    <>
      <div
        onClick={toggleModal}
        style={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          backgroundColor: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
          color: "#fff",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.85";
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "scale(1)";
        }}
        title="Listen to tracks"
      >
        <i 
          className="fas fa-play" 
          style={{ 
            fontSize: "14px",
            marginLeft: "2px",
            pointerEvents: "none"
          }}
        />
      </div>
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fas fa-music" style={{ color: "var(--branding-theme-color, var(--theme-color, #2fbaad))" }}></i>
            <span>Listen to Tracks</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "0" }}>
          {/* <div style={{ padding: "20px", borderBottom: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <p style={{ margin: 0, fontSize: "13px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Overall Progress
            </p>
            {renderProgressBar(overallProgressPercent)}
          </div> */}
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {track_urls.map((track, index) => {
              const audioSource = getAudioSource(track);
              const trackType = getMimeType(track);
              const trackProgress = progressByTrack[track.id] ?? 0;
              return (
                <div 
                  key={track.id} 
                  style={{ 
                    padding: "20px",
                    borderBottom: index < track_urls.length - 1 ? "1px solid #f0f0f0" : "none",
                    transition: "background-color 0.2s ease",
                    backgroundColor: "#fff"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "15px", marginBottom: "12px" }}>
                    <div 
                      style={{
                        minWidth: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        flexShrink: 0
                      }}
                    >
                      {track.order ? track.order + 1 : index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 
                        className="fontWeight700" 
                        style={{ 
                          margin: 0,
                          marginBottom: "4px",
                          fontSize: "15px",
                          color: "#333",
                          lineHeight: "1.4"
                        }}
                      >
                        {track.name}
                      </h6>
                      {track.mix_name && (
                        <p style={{ 
                          margin: 0, 
                          fontSize: "13px", 
                          color: "#666",
                          fontStyle: "italic"
                        }}>
                          {track.mix_name}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* {renderProgressBar(trackProgress)} */}
                  {audioSource ? (
                    <div style={{ 
                      marginLeft: "47px",
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #e9ecef"
                    }}>
                      {failedTracks[track.id] ? (
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "10px",
                          padding: "8px 0"
                        }}>
                          <p style={{ 
                            color: "#dc3545", 
                            fontStyle: "italic", 
                            margin: 0,
                            fontSize: "13px",
                            flex: 1
                          }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: "6px" }}></i>
                            Failed to load audio
                          </p>
                          <button
                            onClick={() => retryLoadTrack(track)}
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              backgroundColor: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "opacity 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.85";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                            }}
                          >
                            <i className="fas fa-redo" style={{ marginRight: "4px" }}></i>
                            Retry
                          </button>
                        </div>
                      ) : (
                        <>
                          {loadingTracks[track.id] && (
                            <div style={{ 
                              fontSize: "12px", 
                              color: "#666", 
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px"
                            }}>
                              <i className="fas fa-spinner fa-spin"></i>
                              Loading audio...
                            </div>
                          )}
                          <audio
                            ref={(el) => {
                              if (el) {
                                audioRefs.current[track.id] = el;
                              }
                            }}
                            controls
                            id={`track_play_${track.id}`}
                            controlsList="nodownload"
                            preload="metadata"
                            onPlay={(event) => handleAudioPlay(track, event)}
                            onPause={() => handlePlayClick(track.id)}
                            onTimeUpdate={(event) => handleProgressUpdate(track, event)}
                            onEnded={(event) => handleEnded(track, event)}
                            onError={(event) => handleAudioError(track, event)}
                            onLoadedMetadata={(event) => handleLoadedMetadata(track, event)}
                            onLoadStart={() => handleLoadStart(track)}
                            onCanPlay={() => handleCanPlay(track)}
                            style={{ 
                              width: "100%", 
                              outline: "none",
                              height: "32px"
                            }}
                          >
                            <source src={audioSource} type={trackType} />
                            Your browser does not support the audio element.
                          </audio>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginLeft: "47px" }}>
                      <p style={{ 
                        color: "#999", 
                        fontStyle: "italic", 
                        margin: 0,
                        fontSize: "13px",
                        padding: "8px 12px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px"
                      }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: "6px" }}></i>
                        Audio source not available
                      </p>
                    </div>
                  )}
              </div>
              );
            })}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ListenTracks;